/**
 * @fileoverview Step Configuration - Defines the 10-step ingestion flow structure.
 *
 * The flow is driven by UNIFIED_STEPS which maps each step index to:
 * - id: Unique step identifier (used in URL query param)
 * - shortLabel: Display name in sidebar stepper
 * - phase: 'creation' or 'ingestion'
 * - trackedSections: Audit sections this step tracks (empty = not audit-tracked)
 */
import type { IngestionSection } from '@/services/types/case-ingestion-types';

import type { UnifiedStepConfig } from '../types';

/**
 * Number of creation-phase steps before matter is created.
 *
 * Step 0 (Basics) is the only creation step — matter creation happens at step 0→1.
 */
export const CREATION_STEP_COUNT = 1;

/**
 * Index of the first audit-tracked ingestion step.
 * Used for returning users (URL has `mid` param).
 */
export const FIRST_INGESTION_STEP_INDEX = 2;

/**
 * Unified step configuration for the combined creation + ingestion flow.
 *
 * Flow breakdown:
 * - Step 0: Creation phase (Type + Basics merged) — not audit-tracked
 * - Step 1: Case Info — post-creation, patches matter, NOT audit-tracked
 * - Steps 2-8: Wizard steps (some audit-tracked via trackedSections, some not)
 * - Step 9: Review — summary of collected data, not audit-tracked
 *
 * The `trackedSections` array indicates which audit sections this step tracks.
 * Empty array = step is not audit-tracked.
 */
export const UNIFIED_STEPS: UnifiedStepConfig[] = [
  // Creation phase
  { index: 0, id: 'basics', shortLabel: 'Basics', phase: 'creation', trackedSections: [] },

  // Ingestion phase
  { index: 1, id: 'case_info', shortLabel: 'Case Info', phase: 'ingestion', trackedSections: [] },
  { index: 2, id: 'client_info', shortLabel: 'Client', phase: 'ingestion', trackedSections: [] },
  { index: 3, id: 'companion_cases', shortLabel: 'Companions', phase: 'ingestion', trackedSections: ['companion_cases'] },
  { index: 4, id: 'liability', shortLabel: 'Police', phase: 'ingestion', trackedSections: ['police_report'] },
  { index: 5, id: 'treatment', shortLabel: 'Treatment', phase: 'ingestion', trackedSections: ['medical_providers'] },
  { index: 6, id: 'coverage', shortLabel: 'Coverage', phase: 'ingestion', trackedSections: ['first_party_coverage', 'third_party_coverage', 'health_insurance'] },
  { index: 7, id: 'documents', shortLabel: 'Documents', phase: 'ingestion', trackedSections: ['retainer_document', 'hipaa_document'] },
  { index: 8, id: 'case_summary', shortLabel: 'Notes', phase: 'ingestion', trackedSections: [] },

  // Review step — summary only, not audit-tracked
  { index: 9, id: 'review', shortLabel: 'Review', phase: 'ingestion', trackedSections: [] },
];

/**
 * Map of section ID to human-readable display label.
 * Used in the completion dialog and review screens to show which sections were completed.
 */
export const SECTION_LABELS: Record<IngestionSection, string> = {
  first_party_coverage: 'First Party Coverage',
  third_party_coverage: 'Third Party Coverage',
  health_insurance: 'Health Insurance',
  police_report: 'Police Report',
  medical_providers: 'Medical Providers',
  retainer_document: 'Retainer Agreement',
  hipaa_document: 'HIPAA Directive',
  companion_cases: 'Companion Cases',
};

/**
 * Derived mapping: section ID → step index.
 * Computed from UNIFIED_STEPS.trackedSections to maintain single source of truth.
 * Used by ReviewStep to navigate to the correct step for a section.
 */
export const SECTION_TO_STEP_INDEX = Object.fromEntries(
  UNIFIED_STEPS.flatMap((step) =>
    step.trackedSections.map((section) => [section, step.index] as const),
  ),
) as Record<IngestionSection, number>;
